// Vokabular für den Satzungszweck (`Contact.zweckNrAO`).
//
// WARUM KONSTANTE UND NICHT KATALOGTABELLE: Die Liste steht im Gesetz. § 52
// Abs. 2 S. 1 Nr. 1–26 AO zählt die gemeinnützigen Zwecke auf; sie ändert sich
// durch den Gesetzgeber, nicht durch Admin-Pflege in den Einstellungen. Genau
// dieselbe Begründung trägt schon `REGISTER_TYPES`
// (`components/forms/validation/handelsregister.ts`). Eine Katalogtabelle
// bräuchte Migration, Seed (der auf PROD ohnehin nur per PR-Label liefe),
// Endpoint und eine Settings-Sektion — für eine Liste, die niemand pflegen
// darf.
//
// WARUM FREITEXT FÜHRT UND DER CODE NUR KLASSIFIZIERT (`Contact.satzungszweck`
// ist das führende Feld):
//
//  1. Der AO-Katalog ist NICHT abschließend. § 52 Abs. 2 S. 2 AO enthält eine
//     Öffnungsklausel: die zuständige Landesfinanzbehörde kann weitere Zwecke
//     für gemeinnützig erklären. „Abschließend" ist die verbreitete Kurzformel
//     und stimmt nicht.
//  2. Er deckt nur GEMEINNÜTZIGE Zwecke. Ein erheblicher Teil des Bestands ist
//     erkennbar nicht gemeinnützig: Bundesverband IT-Mittelstand e.V.,
//     Initiative Aachen e.V., digitalHUB Aachen e.V. sind Wirtschafts-/
//     Berufsverbände (nach § 5 Abs. 1 Nr. 5 KStG steuerbefreit, aber nicht
//     gemeinnützig), dazu reine Geselligkeitsvereine. Deren Zweck kommt im
//     § 52-Katalog schlicht nicht vor — dafür steht `AO_ZWECK_KEIN`.
//  3. § 60 AO i. V. m. Anlage 1 (Mustersatzung) verlangt Zweck UND Art seiner
//     Verwirklichung. Das ist strukturgleich zu Katalog + Tätigkeits-
//     beschreibung bei der Branche: der Code allein trägt die Aussage nicht.
//
// Mehrfachauswahl wird als CSV der `code`-Werte gespeichert („AO52-21,AO52-23")
// — dasselbe Muster wie `tags`, `memberships` und `hobbys`. Die Codes sind
// stabil und dürfen NIE umbenannt werden; die Labels dürfen es.

export type AoZweck = {
  /** Stabiler Speicherwert. Wandert in `Contact.zweckNrAO` (CSV). */
  code: string;
  /** Dropdown-Beschriftung — bewusst kurz, der Gesetzestext ist es nicht. */
  label: string;
  /** Fundstelle, für den Tooltip bzw. die Rückverfolgbarkeit. */
  norm: string;
};

/** Weder gemeinnützig noch mildtätig noch kirchlich (Berufs-/Wirtschaftsverband, Geselligkeitsverein). */
export const AO_ZWECK_KEIN = "KEIN";

/**
 * § 52 Abs. 2 S. 1 Nr. 1–26 AO in der Reihenfolge des Gesetzes, danach die
 * beiden anderen steuerbegünstigten Zwecke (§ 53 mildtätig, § 54 kirchlich) und
 * zuletzt „nicht steuerbegünstigt".
 *
 * Die Nummern 21 (Sport) und 23 (traditionelles Brauchtum einschließlich
 * Karneval) decken den Aachener Bestand unmittelbar ab — Aachener
 * Karnevalsverein 1859, Prinzengarde Richterich, 1. KG Koe Jonge, Maijungen
 * Walheim, KG Tanzgruppe Friesenrath, Aachener Golfclub 1927.
 */
export const AO_ZWECKE: readonly AoZweck[] = [
  { code: "AO52-01", label: "Wissenschaft und Forschung", norm: "§ 52 Abs. 2 Nr. 1 AO" },
  { code: "AO52-02", label: "Religion", norm: "§ 52 Abs. 2 Nr. 2 AO" },
  {
    code: "AO52-03",
    label: "Öffentliches Gesundheitswesen und Gesundheitspflege",
    norm: "§ 52 Abs. 2 Nr. 3 AO",
  },
  { code: "AO52-04", label: "Jugend- und Altenhilfe", norm: "§ 52 Abs. 2 Nr. 4 AO" },
  { code: "AO52-05", label: "Kunst und Kultur", norm: "§ 52 Abs. 2 Nr. 5 AO" },
  { code: "AO52-06", label: "Denkmalschutz und Denkmalpflege", norm: "§ 52 Abs. 2 Nr. 6 AO" },
  {
    code: "AO52-07",
    label: "Erziehung, Volks- und Berufsbildung, Studentenhilfe",
    norm: "§ 52 Abs. 2 Nr. 7 AO",
  },
  {
    code: "AO52-08",
    label: "Naturschutz, Landschaftspflege, Umwelt- und Klimaschutz",
    norm: "§ 52 Abs. 2 Nr. 8 AO",
  },
  { code: "AO52-09", label: "Wohlfahrtswesen", norm: "§ 52 Abs. 2 Nr. 9 AO" },
  {
    code: "AO52-10",
    label: "Hilfe für Verfolgte, Flüchtlinge, Kriegs- und Straftatopfer",
    norm: "§ 52 Abs. 2 Nr. 10 AO",
  },
  { code: "AO52-11", label: "Rettung aus Lebensgefahr", norm: "§ 52 Abs. 2 Nr. 11 AO" },
  {
    code: "AO52-12",
    label: "Feuer-, Arbeits-, Katastrophen- und Zivilschutz, Unfallverhütung",
    norm: "§ 52 Abs. 2 Nr. 12 AO",
  },
  {
    code: "AO52-13",
    label: "Internationale Gesinnung, Toleranz, Völkerverständigung",
    norm: "§ 52 Abs. 2 Nr. 13 AO",
  },
  { code: "AO52-14", label: "Tierschutz", norm: "§ 52 Abs. 2 Nr. 14 AO" },
  { code: "AO52-15", label: "Entwicklungszusammenarbeit", norm: "§ 52 Abs. 2 Nr. 15 AO" },
  {
    code: "AO52-16",
    label: "Verbraucherberatung und Verbraucherschutz",
    norm: "§ 52 Abs. 2 Nr. 16 AO",
  },
  {
    code: "AO52-17",
    label: "Fürsorge für Strafgefangene und ehemalige Strafgefangene",
    norm: "§ 52 Abs. 2 Nr. 17 AO",
  },
  {
    code: "AO52-18",
    label: "Gleichberechtigung von Frauen und Männern",
    norm: "§ 52 Abs. 2 Nr. 18 AO",
  },
  { code: "AO52-19", label: "Schutz von Ehe und Familie", norm: "§ 52 Abs. 2 Nr. 19 AO" },
  { code: "AO52-20", label: "Kriminalprävention", norm: "§ 52 Abs. 2 Nr. 20 AO" },
  { code: "AO52-21", label: "Sport (Schach gilt als Sport)", norm: "§ 52 Abs. 2 Nr. 21 AO" },
  {
    code: "AO52-22",
    label: "Heimatpflege, Heimatkunde, Ortsverschönerung",
    norm: "§ 52 Abs. 2 Nr. 22 AO",
  },
  {
    code: "AO52-23",
    label: "Traditionelles Brauchtum (Karneval, Fastnacht, Fasching)",
    norm: "§ 52 Abs. 2 Nr. 23 AO",
  },
  {
    code: "AO52-24",
    label: "Allgemeine Förderung des demokratischen Staatswesens",
    norm: "§ 52 Abs. 2 Nr. 24 AO",
  },
  { code: "AO52-25", label: "Bürgerschaftliches Engagement", norm: "§ 52 Abs. 2 Nr. 25 AO" },
  {
    code: "AO52-26",
    label: "Friedhöfe und Gedenkstätten",
    norm: "§ 52 Abs. 2 Nr. 26 AO",
  },
  { code: "AO53", label: "Mildtätige Zwecke", norm: "§ 53 AO" },
  { code: "AO54", label: "Kirchliche Zwecke", norm: "§ 54 AO" },
  {
    code: AO_ZWECK_KEIN,
    label: "Nicht steuerbegünstigt (Berufs-/Wirtschaftsverband, Geselligkeitsverein)",
    norm: "§ 5 Abs. 1 Nr. 5 KStG",
  },
];

const BY_CODE = new Map(AO_ZWECKE.map((z) => [z.code, z]));

/** Label zum Code; unbekannte Codes kommen unverändert zurück statt zu verschwinden. */
export const aoZweckLabel = (code: string): string => BY_CODE.get(code)?.label ?? code;

/**
 * CSV → Codes. Unbekannte Codes bleiben erhalten: ein Wert, den eine spätere
 * Gesetzesänderung aus der Liste nimmt, darf beim nächsten Speichern nicht
 * still aus dem Datensatz fallen.
 */
export const parseZweckNrAO = (csv: string | null | undefined): string[] =>
  (csv ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/** Codes → CSV. Dubletten fallen weg, die Reihenfolge der Eingabe bleibt. */
export const serializeZweckNrAO = (codes: readonly string[]): string | null => {
  const out: string[] = [];
  for (const c of codes) {
    const v = c.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out.length ? out.join(",") : null;
};

/** Position jedes Katalog-Codes in der Gesetzesreihenfolge. */
const RANG = new Map(AO_ZWECKE.map((z, i) => [z.code, i]));

/**
 * Codes in die Reihenfolge des Gesetzes bringen — nicht die der Klicks.
 * Unbekannte Codes (nicht oder nicht mehr im Katalog) bleiben erhalten und
 * stehen in ihrer bisherigen Reihenfolge HINTER den bekannten; ein Wert, den
 * die Maske nicht kennt, darf beim Speichern nicht still verschwinden.
 */
export const zweckeOrdnen = (codes: readonly string[]): string[] => {
  const ohneDubletten = parseZweckNrAO(serializeZweckNrAO(codes));
  const bekannt = ohneDubletten
    .filter((c) => RANG.has(c))
    .sort((a, b) => (RANG.get(a) ?? 0) - (RANG.get(b) ?? 0));
  const unbekannt = ohneDubletten.filter((c) => !RANG.has(c));
  return [...bekannt, ...unbekannt];
};

/** CSV um einen Code ergänzen; Ergebnis in Gesetzesreihenfolge. */
export const zweckHinzufuegen = (csv: string | null | undefined, code: string): string | null =>
  serializeZweckNrAO(zweckeOrdnen([...parseZweckNrAO(csv), code]));

/** Code aus der CSV entfernen; die übrige Reihenfolge bleibt. Leer → null. */
export const zweckEntfernen = (csv: string | null | undefined, code: string): string | null =>
  serializeZweckNrAO(parseZweckNrAO(csv).filter((c) => c !== code));
